<template>
  <div>
    <transition name="osl-fade">
      <div v-if="visible" class="osl-drawer-backdrop" @click="$emit('close')"></div>
    </transition>
    <transition name="osl-slide">
      <aside v-if="visible" class="osl-drawer" role="dialog" aria-label="Odoo log detail">
        <!-- Header -->
        <header class="osl-drawer__header">
          <div class="osl-drawer__title-wrap">
            <div class="osl-drawer__eyebrow">
              <span v-if="log">{{ jobMeta.group }} · {{ log.job_type }}</span>
              <span v-else>Loading…</span>
            </div>
            <div class="osl-drawer__title">
              <template v-if="log">
                {{ jobMeta.label }}
                <span v-if="jobMeta.action" class="osl-muted">· {{ jobMeta.action }}</span>
                <span :class="['osl-badge', statusMeta.cls, 'ml-2']">{{ statusMeta.label }}</span>
                <span v-if="log.is_duplicate === 'Y'" class="osl-badge osl-badge--neutral ml-1" title="Odoo reported this record already exists. It is treated as synced.">Duplicate</span>
                <span v-if="log.is_superseded === 'Y'" class="osl-badge osl-badge--neutral ml-1" title="A newer attempt exists for this request">Superseded</span>
              </template>
            </div>
            <div v-if="log" class="osl-drawer__uid">
              <span class="osl-mono">{{ log.request_uid }}</span>
              <button class="osl-icon-btn" title="Copy request ID" @click="copy(log.request_uid, 'Request ID')">
                <i class="fa fa-clone"></i>
              </button>
            </div>
          </div>
          <button class="osl-icon-btn osl-icon-btn--lg" title="Close (Esc)" @click="$emit('close')">
            <i class="fa fa-times"></i>
          </button>
        </header>

        <div v-if="loading" class="osl-drawer__loading">
          <div class="osl-spinner"></div>
          <span>Loading log…</span>
        </div>

        <div v-else-if="loadError" class="osl-drawer__body">
          <div class="osl-callout osl-callout--danger">
            <i class="fa fa-exclamation-triangle"></i>
            <div>{{ loadError }}</div>
          </div>
        </div>

        <div v-else-if="log" class="osl-drawer__body">
          <!-- Actions -->
          <div class="osl-drawer__actions">
            <button
              class="osl-btn osl-btn--primary"
              :disabled="!!resendBlockedReason"
              :title="resendBlockedReason || 'Re-run the original job with current data'"
              @click="$emit('resend', { rows: [log], mode: 'RERUN' })"
            >
              <i class="fa fa-refresh"></i> Re-run job
            </button>
            <button
              class="osl-btn osl-btn--secondary"
              :disabled="!!replayBlockedReason"
              :title="replayBlockedReason || 'Send the exact stored request again'"
              @click="$emit('resend', { rows: [log], mode: 'REPLAY' })"
            >
              <i class="fa fa-repeat"></i> Replay request
            </button>
            <button class="osl-btn osl-btn--ghost" title="Reload" @click="load(requestUid)">
              <i class="fa fa-rotate-right"></i>
            </button>
            <span v-if="resendBlockedReason" class="osl-muted osl-small ml-1">{{ resendBlockedReason }}</span>
          </div>

          <!-- Errors -->
          <div v-if="log.status !== 'Completed' && log.error_message" class="osl-callout osl-callout--danger">
            <i class="fa fa-exclamation-circle"></i>
            <div>
              <div class="osl-callout__title">Odoo error</div>
              <div class="osl-callout__text">{{ log.error_message }}</div>
            </div>
          </div>
          <div v-if="log.is_duplicate === 'Y' && log.error_message" class="osl-callout osl-callout--neutral">
            <i class="fa fa-info-circle"></i>
            <div>
              <div class="osl-callout__title">Duplicate (treated as synced)</div>
              <div class="osl-callout__text">{{ log.error_message }}</div>
            </div>
          </div>
          <div v-if="log.resend_status === 'Failed' && log.resend_error" class="osl-callout osl-callout--warning">
            <i class="fa fa-history"></i>
            <div>
              <div class="osl-callout__title">Last resend failed</div>
              <div class="osl-callout__text">{{ log.resend_error }}</div>
            </div>
          </div>
          <div v-if="isQueued" class="osl-callout osl-callout--info">
            <i class="fa fa-clock-o"></i>
            <div>
              <div class="osl-callout__title">Resend {{ log.resend_status.toLowerCase() }} ({{ log.resend_mode }})</div>
              <div class="osl-callout__text">
                Runs on <b>{{ sourceLabel(log.source_cinema_id) }}</b>.
                <template v-if="Number(log.source_cinema_id) !== 0">
                  That cinema server picks it up within about 2 minutes once it's online.
                </template>
              </div>
            </div>
          </div>

          <!-- Meta grid -->
          <div class="osl-meta">
            <div class="osl-meta__item">
              <div class="osl-meta__k">Job ID</div>
              <div class="osl-meta__v osl-mono">
                {{ log.job_id || "—" }}
                <button v-if="log.job_id" class="osl-icon-btn osl-icon-btn--xs" @click="copy(log.job_id, 'Job ID')"><i class="fa fa-clone"></i></button>
              </div>
            </div>
            <div class="osl-meta__item">
              <div class="osl-meta__k">Reference</div>
              <div class="osl-meta__v osl-mono">{{ log.reference_id || "—" }}</div>
            </div>
            <div class="osl-meta__item">
              <div class="osl-meta__k">Cinema</div>
              <div class="osl-meta__v">{{ cinemaLabel(log.cinema_id) }}</div>
            </div>
            <div class="osl-meta__item">
              <div class="osl-meta__k">Executed on</div>
              <div class="osl-meta__v">
                <span :class="['osl-tag', Number(log.source_cinema_id) === 0 ? 'osl-tag--blue' : 'osl-tag--violet']">
                  {{ sourceLabel(log.source_cinema_id) }}
                </span>
              </div>
            </div>
            <div class="osl-meta__item osl-meta__item--wide">
              <div class="osl-meta__k">Endpoint</div>
              <div class="osl-meta__v osl-mono">
                <span class="osl-method">{{ log.http_method }}</span> {{ log.endpoint || "—" }}
              </div>
            </div>
            <div class="osl-meta__item">
              <div class="osl-meta__k">HTTP</div>
              <div class="osl-meta__v">
                <span :class="['osl-http', httpClass(log.http_status)]">{{ log.http_status || "No response" }}</span>
              </div>
            </div>
            <div class="osl-meta__item">
              <div class="osl-meta__k">Duration</div>
              <div class="osl-meta__v">{{ formatDuration(log.duration_ms) }}</div>
            </div>
            <div class="osl-meta__item">
              <div class="osl-meta__k">Trigger</div>
              <div class="osl-meta__v">
                <span :class="triggerMeta(log.trigger_source).cls">{{ triggerMeta(log.trigger_source).label }}</span>
                <span v-if="Number(log.retry_count) > 0" class="osl-muted osl-small ml-1">attempt #{{ Number(log.retry_count) + 1 }}</span>
              </div>
            </div>
            <div class="osl-meta__item">
              <div class="osl-meta__k">Created</div>
              <div class="osl-meta__v" :title="log.created_at">{{ fmt(log.created_at) }}</div>
            </div>
            <div class="osl-meta__item">
              <div class="osl-meta__k">Updated</div>
              <div class="osl-meta__v" :title="log.updated_at">{{ fmt(log.updated_at) }}</div>
            </div>
            <div v-if="log.resend_requested_at" class="osl-meta__item">
              <div class="osl-meta__k">Resend requested</div>
              <div class="osl-meta__v">{{ fmt(log.resend_requested_at) }}</div>
            </div>
          </div>

          <!-- Tabs -->
          <div class="osl-tabs">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              :class="['osl-tabs__tab', { 'is-active': activeTab === tab.key }]"
              @click="activeTab = tab.key"
            >
              {{ tab.label }}
              <span v-if="tab.count !== undefined" class="osl-tabs__count">{{ tab.count }}</span>
            </button>
          </div>

          <div v-if="activeTab !== 'attempts'" class="osl-code">
            <div class="osl-code__bar">
              <span class="osl-muted osl-small">{{ activeTabHint }}</span>
              <div>
                <label class="osl-check osl-small mr-2">
                  <input v-model="wrap" type="checkbox" /> Wrap
                </label>
                <button class="osl-btn osl-btn--ghost osl-btn--xs" :disabled="!activeContent" @click="copy(activeContent, 'JSON')">
                  <i class="fa fa-clone"></i> Copy
                </button>
              </div>
            </div>
            <pre :class="['osl-code__pre', { 'is-wrap': wrap }]">{{ activeContent || "No data stored" }}</pre>
          </div>

          <div v-else class="osl-timeline">
            <div
              v-for="(attempt, idx) in attempts"
              :key="attempt.request_uid"
              :class="['osl-timeline__item', { 'is-current': attempt.request_uid === log.request_uid }]"
              @click="attempt.request_uid !== log.request_uid && $emit('open', attempt.request_uid)"
            >
              <div :class="['osl-timeline__dot', 'is-' + String(attempt.status).toLowerCase()]"></div>
              <div class="osl-timeline__content">
                <div class="osl-timeline__row">
                  <b>#{{ idx + 1 }}</b>
                  <span :class="triggerMeta(attempt.trigger_source).cls">{{ triggerMeta(attempt.trigger_source).label }}</span>
                  <span :class="['osl-badge', statusMetaOf(attempt.status).cls]">{{ statusMetaOf(attempt.status).label }}</span>
                  <span class="osl-muted osl-small">{{ fmt(attempt.created_at) }}</span>
                  <span class="osl-muted osl-small">· {{ formatDuration(attempt.duration_ms) }}</span>
                  <span v-if="attempt.request_uid === log.request_uid" class="osl-tag ml-auto">Viewing</span>
                </div>
                <div v-if="attempt.error_message && attempt.status !== 'Completed'" class="osl-timeline__err">
                  {{ attempt.error_message }}
                </div>
                <div class="osl-mono osl-small osl-muted">{{ attempt.request_uid }}</div>
              </div>
            </div>
            <div v-if="!attempts.length" class="osl-muted osl-small p-2">No attempts recorded.</div>
          </div>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
import moment from "moment";
import OdooLogsService from "@/services/OdooLogsService";
import {
  jobTypeMeta,
  STATUS_META,
  TRIGGER_META,
  prettyJSON,
  formatDuration,
} from "./odooLogsMeta";

export default {
  name: "OdooLogDetailDrawer",
  props: {
    visible: { type: Boolean, default: false },
    requestUid: { type: String, default: null },
    cinemaMap: { type: Object, default: () => ({}) },
    resendSupportedTypes: { type: Array, default: () => [] },
    refreshKey: { type: Number, default: 0 },
  },
  data() {
    return {
      log: null,
      loading: false,
      loadError: null,
      activeTab: "request",
      wrap: false,
    };
  },
  computed: {
    jobMeta() {
      return jobTypeMeta(this.log && this.log.job_type);
    },
    statusMeta() {
      return this.statusMetaOf(this.log && this.log.status);
    },
    attempts() {
      return (this.log && this.log.attempts) || [];
    },
    isQueued() {
      return this.log && ["Queued", "Processing"].indexOf(this.log.resend_status) > -1;
    },
    resendBlockedReason() {
      if (!this.log) return "Loading";
      if (this.log.is_superseded === "Y") return "A newer attempt exists. Resend from the latest attempt.";
      if (this.isQueued) return `Resend already ${this.log.resend_status.toLowerCase()}`;
      if (this.resendSupportedTypes.length && this.resendSupportedTypes.indexOf(this.log.job_type) === -1)
        return "Re-run isn't supported for this job type";
      return null;
    },
    replayBlockedReason() {
      if (!this.log) return "Loading";
      if (this.log.is_superseded === "Y") return "A newer attempt exists. Resend from the latest attempt.";
      if (this.isQueued) return `Resend already ${this.log.resend_status.toLowerCase()}`;
      if (!this.log.endpoint || !this.log.request_payload) return "No stored request to replay";
      return null;
    },
    tabs() {
      return [
        { key: "request", label: "Request" },
        { key: "response", label: "Response" },
        { key: "payload", label: "Job payload" },
        { key: "attempts", label: "Attempts", count: this.attempts.length },
      ];
    },
    activeContent() {
      if (!this.log) return "";
      if (this.activeTab === "request") return prettyJSON(this.log.request_payload);
      if (this.activeTab === "response") return prettyJSON(this.log.response_payload);
      if (this.activeTab === "payload") return prettyJSON(this.log.job_payload);
      return "";
    },
    activeTabHint() {
      if (this.activeTab === "request") return "Body sent to Odoo (auth token isn't stored)";
      if (this.activeTab === "response") return "Raw body returned by Odoo";
      if (this.activeTab === "payload") return "Business payload used to rebuild the job on Re-run";
      return "";
    },
  },
  watch: {
    requestUid: {
      immediate: true,
      handler(uid) {
        if (uid && this.visible) this.load(uid);
      },
    },
    visible(val) {
      if (val && this.requestUid) this.load(this.requestUid);
      if (!val) {
        this.log = null;
        this.activeTab = "request";
      }
    },
    refreshKey() {
      if (this.visible && this.requestUid) this.load(this.requestUid, true);
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    formatDuration,
    triggerMeta(trigger) {
      return TRIGGER_META[trigger] || TRIGGER_META.AUTO;
    },
    statusMetaOf(status) {
      return STATUS_META[status] || { label: status || "—", cls: "osl-badge--neutral" };
    },
    onKeydown(e) {
      if (this.visible && (e.key === "Escape" || e.keyCode === 27)) this.$emit("close");
    },
    load(uid, silent = false) {
      if (!silent) {
        this.loading = true;
        this.loadError = null;
      }
      OdooLogsService.GetOdooApiLogDetail(uid)
        .then(({ data }) => {
          if (uid !== this.requestUid) return;
          if (data && data.status) {
            this.log = data.Records;
            this.loadError = null;
          } else {
            this.loadError = (data && data.message) || "Unable to load log";
          }
        })
        .catch((err) => {
          this.loadError = (err && err.message) || "Unable to load log";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cinemaLabel(id) {
      if (id === null || id === undefined) return "—";
      if (Number(id) === 0) return "Cloud / HO";
      return this.cinemaMap[id] || `Cinema #${id}`;
    },
    sourceLabel(id) {
      if (id === null || id === undefined || Number(id) === 0) return "Cloud server";
      return `${this.cinemaMap[id] || "Cinema #" + id} server`;
    },
    httpClass(code) {
      if (!code) return "is-none";
      if (code >= 200 && code < 300) return "is-ok";
      if (code >= 400 && code < 500) return "is-warn";
      return "is-err";
    },
    fmt(date) {
      if (!date) return "—";
      return moment(date).format("DD MMM YYYY, HH:mm:ss");
    },
    copy(text, label) {
      if (!text) return;
      this.$copyText(text).then(
        () => this.$toasted.show(`${label} copied`),
        () => this.$toasted.show("Copy failed"),
      );
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/Pages/MasterContainer/OdooSyncLogs/odooLogDetailDrawer.vue